import dynamic from 'helpers/dynamic';
import type { IProductVideo } from 'autogen/swagger/all';
import { SeeVideo } from 'autogen/translation-keys/trans-website-product';
import { Loading, Video } from 'autogen/translation-keys/trans-website-shared';
import ArticleCard from 'components/ArticleCard';
import useVideoDetails, { type VideoDetailsProps } from 'components/video/hooks/useVideoDetails';
import ReactLoadingSpinner from 'modules/loadingSpinner';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { GA4Tracking } from 'modules/tracking/GA4';

const PlayIcon = dynamic(() => import('icons/Play.svg?react'));

/* #region  styles */
const ImageContainer = styled.div`
    position: relative;
    width: 120px;
    height: 120px;
`;

const StyledImg = styled.img`
    object-fit: cover;
    object-position: center;
    min-width: 120px;
    min-height: 120px;
    max-width: 100%;
    max-height: 100%;
`;

const StyledLoading = styled(ReactLoadingSpinner)`
    width: 100%;
    height: 100%;
    min-height: auto;
    min-width: auto;
`;

const StyledPlayIcon = styled(PlayIcon)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const StyledArticleCard = styled(ArticleCard)`
    cursor: pointer;
`;
/* #endregion */

const openVideoModal = async (url: string, platform: IProductVideo['platform']) => {
    const { default: videoModal } = await import(/* webpackChunkName: "videoModal" */ 'modules/modal/videoModal');

    const modal = videoModal();

    modal({
        url,
        platform
    });
};

export default function HorizontalVideoArticleCard({
    videoModel: { id, platform, type }
}: {
    videoModel: IProductVideo;
}) {
    const video: VideoDetailsProps = { id, platform, subtitles: true };
    const videoDetails = useVideoDetails(video);
    const { t } = useTranslation();

    // Loading..
    if (!videoDetails)
        return (
            <ArticleCard
                customTheme={{ layoutTheme: 'horizontal', colorTheme: 'nordicGrey60' }}
                textBox={{
                    overline: t(Video), // should be some loading skeleton
                    title: t(Loading), // should be some loading skeleton
                    action: { buttonText: t(SeeVideo) }
                }}
            >
                <ImageContainer>
                    <StyledLoading />
                </ImageContainer>
            </ArticleCard>
        );

    const { title, duration, videoUrl, thumbnail } = videoDetails;

    return (
        <StyledArticleCard
            customTheme={{ layoutTheme: 'horizontal', colorTheme: 'nordicGrey60' }}
            textBox={{
                overline: duration,
                title,
                action: {
                    buttonText: t(SeeVideo)
                }
            }}
            onClick={() => {
                GA4Tracking({
                    eventName: 'open_modal',
                    context: 'product_page',
                    category: 'advisory',
                    eventParams: [{ param_name: 'modal_type', param_value: 'video' }]
                });
                openVideoModal(videoUrl, platform);
            }}
        >
            <ImageContainer>
                <StyledPlayIcon />
                <StyledImg alt="thumbnail" src={thumbnail} width="120px" height="120px" />
            </ImageContainer>
        </StyledArticleCard>
    );
}
